import { useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { SigningListItem } from "../types";
import { SigningDetailPanel } from "./SigningDetailPanel";

/** Marks a list row, so pressing one switches the sheet instead of closing it. */
export const SIGNING_ROW_ATTRIBUTE = "data-signing-row";

interface SigningDetailSheetProps {
  /** The selected row; `null` closes the sheet. */
  item: SigningListItem | null;
  onClose: () => void;
  onSignTurn: (sessionId: string) => void;
}

/**
 * The selected row's detail panel, slid in from the right OVER the list — the
 * same right-hand shell as the Documents page's signature and comments panels.
 * It used to sit in a grid column beside the table, which took 344px from the
 * rows for as long as the page was open and pushed the status column under a
 * horizontal scrollbar; there was also no way to put it away.
 *
 * Non-modal and without a backdrop, so the list stays usable underneath:
 * pressing another row moves the sheet to that row, while Esc, the close
 * button or a press anywhere else closes it.
 */
export function SigningDetailSheet({ item, onClose, onSignTurn }: SigningDetailSheetProps) {
  const { t } = useLanguage();
  // Keep painting the last row while the sheet slides out — `item` is already
  // null by then, and an empty sheet sliding away reads as a glitch.
  // (Adjusted during render, React's sanctioned way to derive state from props.)
  const [shown, setShown] = useState(item);
  if (item && item !== shown) setShown(item);
  // A press outside dismissed the sheet: focus went (or deliberately did not
  // go) where the user pressed, and must not be pulled back into the list.
  const dismissedOutside = useRef(false);

  return (
    <DialogPrimitive.Root
      open={item !== null}
      modal={false}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          className="fixed inset-y-0 right-0 z-50 flex h-full w-full max-w-md flex-col border-l bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right"
          aria-describedby={undefined}
          // Focus stays on the row that was pressed, so the keyboard keeps
          // walking the list; Esc closes the sheet from there all the same.
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => {
            // Radix would refocus whatever held focus at open, even when the
            // sheet closed because the user pressed something else. Only a
            // close that dropped focus (the × unmounting under it) is handed
            // back to the row the sheet was showing — or, when a filter has
            // hidden that row, to the first row on screen, so keyboard users
            // keep a place in the list.
            event.preventDefault();
            if (dismissedOutside.current) {
              dismissedOutside.current = false;
              return;
            }
            const active = document.activeElement;
            if (active && active !== document.body) return;
            const row = (shown && rowElement(shown.key)) ?? firstRow();
            row?.focus();
          }}
          onPointerDownOutside={(event) => {
            const target = event.target as Element | null;
            if (target?.closest?.(`[${SIGNING_ROW_ATTRIBUTE}]`)) event.preventDefault();
            else dismissedOutside.current = true;
          }}
          // Never close on focus leaving: the panel's own Cancel confirmation
          // and the viewers it opens take focus, and closing here would unmount
          // the very panel that owns them.
          onFocusOutside={(event) => event.preventDefault()}
        >
          <div className="flex flex-shrink-0 items-center justify-between gap-2 px-5 py-4">
            <DialogPrimitive.Title className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t.signing.panel.eyebrow}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close
              aria-label={t.common.close}
              className="rounded-sm p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {shown && <SigningDetailPanel item={shown} onSignTurn={onSignTurn} />}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function firstRow(): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[${SIGNING_ROW_ATTRIBUTE}]`);
}

function rowElement(key: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[${SIGNING_ROW_ATTRIBUTE}="${CSS.escape(key)}"]`);
}
